import { http } from '@/api/http';
import type { PageEnvelope } from '@/api/shared';

/**
 * Raw transport for the browse-all questionnaires list. The generated `schema.ts`
 * now carries `QuestionnaireSummary`; this slice DTO is kept hand-written (all-optional
 * fields) to stay tolerant of partial rows from the mock and future contract drift.
 */

export type QuestionnaireStatusCode =
  | 'NOT_SENT'
  | 'SENT'
  | 'OPENED'
  | 'COMPLETED'
  | 'EXPIRED'
  | 'REVOKED';

export type QuestionnaireDeliveryCode = 'EMAIL' | 'SMS' | 'URL';

/** A browse-all row: a questionnaire request with names + daysOut resolved server-side. */
export interface QuestionnaireSummaryDto {
  id?: string;
  person_guid?: string;
  patient_name?: string | null;
  template_id?: string;
  template_name?: string | null;
  status?: QuestionnaireStatusCode;
  delivery_method?: QuestionnaireDeliveryCode | null;
  sent_at?: string | null;
  sent_by_name?: string | null;
  /** Guid of the creating user (request author); resolved to a display name client-side. */
  created_by?: string | null;
  completed_at?: string | null;
  days_out?: number | null;
  /** Latest delivery-attempt status (QUEUED/SENT/DELIVERED/…), or null if none. */
  delivery_status?: string | null;
}

export type QuestionnaireSummaryPageDto = PageEnvelope<QuestionnaireSummaryDto>;

export interface BrowseQuestionnairesParams {
  readonly page?: number;
  readonly size?: number;
  readonly status?: QuestionnaireStatusCode;
  /** Template guid filter. */
  readonly templateId?: string;
  /** Patient last-name substring (min 2 chars; server ignores shorter). */
  readonly q?: string;
  /** Creating user's guid (request author). */
  readonly createdBy?: string;
  readonly signal?: AbortSignal;
}

export const browseQuestionnairesEndpoints = {
  /** GET /questionnaires — server-paginated browse-all with optional status/template/search filters. */
  browseQuestionnaires: (params?: BrowseQuestionnairesParams): Promise<QuestionnaireSummaryPageDto> => {
    const search = new URLSearchParams();
    if (params?.page !== undefined) search.set('page', String(params.page));
    if (params?.size !== undefined) search.set('size', String(params.size));
    if (params?.status !== undefined) search.set('status', params.status);
    if (params?.templateId !== undefined && params.templateId.length > 0) {
      search.set('template_id', params.templateId);
    }
    if (params?.q !== undefined && params.q.length > 0) search.set('q', params.q);
    if (params?.createdBy !== undefined && params.createdBy.length > 0) {
      search.set('created_by', params.createdBy);
    }
    const query = search.toString();
    const path = query.length > 0 ? `/questionnaires?${query}` : '/questionnaires';
    return http.get<QuestionnaireSummaryPageDto>(
      path,
      params?.signal !== undefined ? { signal: params.signal } : undefined,
    );
  },
};
